<template>
  <div>
    <label
      class="relative mt-2 flex w-full"
      :class="{
        'form-input-error text-error': hasError,
        'text-main': !hasError,
        'cursor-pointer': !inputDisabled,
        'cursor-not-allowed': inputDisabled,
      }"
    >
      <SvgIcon v-if="props.icon" :path="props.icon" :size="25" class="opacity-60" />
      <span class="relative ml-2 w-full border-b border-strong pb-1 text-body-m">
        <p :class="{ 'txt-label-s-regular text-subtle': props.modelValue }">{{ props.label || 'Upload file' }}</p>
        <p v-if="props.modelValue" class="text-body-m">{{ props.modelValue.file.name }}</p>
      </span>
      <input
        ref="fileInput"
        data-testid="file-upload-async-input"
        class="sr-only"
        type="file"
        :accept="props.accept"
        :disabled="inputDisabled"
        :aria-invalid="hasError"
        :aria-describedby="statusId"
        :test-data-error="hasError || null"
        @focus="focus"
        @blur="blur"
        @change="handleInputChange"
      />
      <div v-if="state === 'uploading'" data-testid="file-upload-async-progress" class="mt-3">
        <CircleProgress :progress="uploadProgress" size="40" />
      </div>
    </label>
    <div :id="statusId" aria-live="polite" data-testid="file-upload-async-status" class="mt-2 txt-body-s-regular">
      <p v-if="state === 'preparing'" class="text-subtle">{{ t('FileUploadAsync.preparing') }}</p>
      <p v-else-if="state === 'uploading'" class="text-subtle">{{ t('FileUploadAsync.uploading') }}</p>
      <p v-else-if="state === 'uploaded'" class="text-success">{{ t('FileUploadAsync.uploaded') }}</p>
      <p v-else-if="state === 'error'" role="alert" class="text-error">{{ failureMessage || t(`FileUploadAsync.${failureReason}`) }}</p>
    </div>
    <div v-if="state === 'error' || state === 'preparing'" class="mt-2 flex gap-2">
      <button
        v-if="state === 'error'"
        type="button"
        class="btn-secondary-color-s"
        :disabled="disabled"
        data-testid="file-upload-async-retry"
        @click="uploadSelectedFile"
      >
        {{ t('FileUploadAsync.retry') }}
      </button>
      <button type="button" class="btn-tertiary-neutral-s" :disabled="disabled" data-testid="file-upload-async-discard" @click="discard">
        {{ t('FileUploadAsync.discard') }}
      </button>
    </div>
    <p v-if="props.hint" :class="{ 'ml-8': props.icon, 'text-error': error }" class="mt-2 text-label-s opacity-60">{{ hint }}</p>
  </div>
</template>

<script setup lang="ts">
import { FileChecksum } from '@rails/activestorage/src/file_checksum'
import { CircleProgress } from '@ramp106/omrjs-core-ui'
import * as Sentry from '@sentry/vue'
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCreateDirectUploadMutation } from '@/gql/myomr'
import type { DirectUploadFile, DirectUploadState } from '@/types'

interface Props {
  label?: string
  icon?: string
  modelValue?: DirectUploadFile
  accept?: string
  hint?: string
  error?: boolean
  disabled?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: DirectUploadFile | undefined): void
  (e: 'state-change', value: DirectUploadState): void
  (e: 'blur'): void
  (e: 'focus'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()
const statusId = useId()
const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File>()
const state = ref<DirectUploadState>(props.modelValue ? 'uploaded' : 'idle')
const uploadProgress = ref(0)
const failureReason = ref<'failed' | 'stalled' | 'aborted'>('failed')
const failureMessage = ref('')
const busy = computed(() => state.value === 'preparing' || state.value === 'uploading')
const inputDisabled = computed(() => props.disabled || busy.value)
const hasError = computed(() => props.error || state.value === 'error')
const { mutate: createDirectUpload } = useCreateDirectUploadMutation({})
const INACTIVITY_TIMEOUT = 120_000
let request: XMLHttpRequest | undefined
let inactivityTimer: ReturnType<typeof setTimeout> | undefined
let attempt = 0

watch(state, (value) => emit('state-change', value), { flush: 'sync', immediate: true })
watch(
  () => props.modelValue,
  (value) => {
    if (!value && state.value === 'uploaded') {
      selectedFile.value = undefined
      if (fileInput.value) fileInput.value.value = ''
      state.value = 'idle'
    }
  },
)

function blur() {
  emit('blur')
}

function focus() {
  emit('focus')
}

function calculateChecksum(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    FileChecksum.create(file, (error, checksum) => {
      if (error) reject(new Error('Checksum failed'))
      else resolve(checksum)
    })
  })
}

function handleInputChange() {
  if (inputDisabled.value) return
  const file = fileInput.value?.files?.[0]
  if (!file) return
  selectedFile.value = file
  void uploadSelectedFile()
}

async function uploadSelectedFile() {
  const file = selectedFile.value
  if (!file || inputDisabled.value) return
  const currentAttempt = ++attempt
  state.value = 'preparing'
  uploadProgress.value = 0
  failureReason.value = 'failed'
  failureMessage.value = ''
  emit('update:modelValue', undefined)

  try {
    const checksum = await calculateChecksum(file)
    if (currentAttempt !== attempt) return
    const response = await Promise.race([
      createDirectUpload({
        byteSize: file.size,
        checksum,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
      }),
      new Promise<never>((_resolve, reject) => {
        inactivityTimer = setTimeout(() => {
          if (currentAttempt !== attempt) return
          failureReason.value = 'stalled'
          reject(new Error('Upload preparation timed out'))
        }, INACTIVITY_TIMEOUT)
      }),
    ])
    if (currentAttempt !== attempt) return
    clearTimeout(inactivityTimer)
    const payload = response?.data?.createDirectUpload
    failureMessage.value = payload?.errors?.join(' ') || ''
    const directUpload = payload?.directUpload
    if (failureMessage.value) throw new Error('Upload preparation rejected')
    if (!directUpload) throw new Error('Upload preparation failed')
    state.value = 'uploading'
    await uploadFileToURL(file, directUpload.url, directUpload.headers, currentAttempt)
    if (currentAttempt !== attempt) return
    uploadProgress.value = 100
    emit('update:modelValue', { file, blobId: directUpload.signedBlobId })
    state.value = 'uploaded'
  } catch {
    if (currentAttempt !== attempt) return
    const stage = state.value
    state.value = 'error'
    Sentry.withScope((scope) => {
      scope.addEventProcessor((event) => ({ ...event, breadcrumbs: [] }))
      Sentry.captureException(new Error('Direct upload failed'), {
        tags: { uploadStage: stage, uploadFailure: failureReason.value },
        extra: { httpStatus: request?.status ?? 0, fileSize: file.size, mimeType: file.type },
      })
    })
  } finally {
    if (currentAttempt === attempt) {
      clearTimeout(inactivityTimer)
      request = undefined
    }
  }
}

function uploadFileToURL(file: File, url: string, headers: string, currentAttempt: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const signedHeaders: Record<string, string> = JSON.parse(headers)
    const xhr = new XMLHttpRequest()
    request = xhr
    xhr.open('PUT', url)
    for (const [key, value] of Object.entries(signedHeaders)) {
      xhr.setRequestHeader(key.toLowerCase(), value)
    }
    xhr.setRequestHeader('content-type', '')

    function resetInactivityTimer() {
      clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(() => {
        failureReason.value = 'stalled'
        xhr.abort()
      }, INACTIVITY_TIMEOUT)
    }

    let bytesLoaded = 0
    xhr.upload.addEventListener('progress', (event) => {
      if (currentAttempt !== attempt) return
      if (event.loaded > bytesLoaded) {
        bytesLoaded = event.loaded
        resetInactivityTimer()
      }
      if (event.lengthComputable && event.total > 0) {
        uploadProgress.value = Math.min(99, Math.floor((event.loaded / event.total) * 100))
      }
    })
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error('Storage rejected upload'))
    }
    xhr.onerror = () => reject(new Error('Upload network error'))
    xhr.onabort = () => {
      if (currentAttempt === attempt && failureReason.value !== 'stalled') failureReason.value = 'aborted'
      reject(new Error('Upload aborted'))
    }
    resetInactivityTimer()
    xhr.send(file)
  })
}

function discard() {
  if (props.disabled || state.value === 'uploading') return
  attempt++
  clearTimeout(inactivityTimer)
  failureMessage.value = ''
  selectedFile.value = undefined
  if (fileInput.value) fileInput.value.value = ''
  emit('update:modelValue', undefined)
  state.value = 'idle'
}

onBeforeUnmount(() => {
  attempt++
  clearTimeout(inactivityTimer)
  request?.abort()
})
</script>
